import { DebugLogger } from '@affine/debug';
import type { GetWorkspaceConfigQuery, InviteLink } from '@affine/graphql';
import {
  catchErrorInto,
  effect,
  Entity,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  smartRetry,
} from '@toeverything/infra';
import { exhaustMap, tap } from 'rxjs';

import type { WorkspaceRealtimeService } from '../../editor/services/workspace-realtime.service';
import type { WorkspaceService } from '../../workspace';
import type { WorkspaceShareSettingStore } from '../stores/share-setting';

type EnableAi = GetWorkspaceConfigQuery['workspace']['enableAi'];
type EnableUrlPreview =
  GetWorkspaceConfigQuery['workspace']['enableUrlPreview'];
// sl-xm1c.2.4: Workspace-Admin Toggle for the AppBases feature
type AppBasesEnabled = GetWorkspaceConfigQuery['workspace']['appBasesEnabled'];

const logger = new DebugLogger('affine:workspace-permission');

export class WorkspaceShareSetting extends Entity {
  enableAi$ = new LiveData<EnableAi | null>(null);
  enableUrlPreview$ = new LiveData<EnableUrlPreview | null>(null);
  // sl-xm1c.2.4: Workspace-Admin Toggle for the AppBases feature
  appBasesEnabled$ = new LiveData<AppBasesEnabled | null>(null);
  presenceDiscoveryEnabled$ = new LiveData<boolean | null>(null);
  inviteLink$ = new LiveData<InviteLink | null>(null);
  isLoading$ = new LiveData(false);
  error$ = new LiveData<any>(null);

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly store: WorkspaceShareSettingStore,
    private readonly realtime: WorkspaceRealtimeService
  ) {
    super();
    const subscription = realtime.presenceDiscoveryEnabled$.subscribe(
      enabled => {
        if (enabled !== null) this.presenceDiscoveryEnabled$.next(enabled);
      }
    );
    this.disposables.push(() => subscription.unsubscribe());
    if (workspaceService.workspace.flavour === 'local')
      this.presenceDiscoveryEnabled$.next(false);
    else this.revalidate();
  }

  revalidate = effect(
    exhaustMap(() => {
      return fromPromise(signal =>
        this.store.fetchWorkspaceConfig(
          this.workspaceService.workspace.id,
          signal
        )
      ).pipe(
        smartRetry(),
        tap(value => {
          if (value) {
            this.enableAi$.next(value.enableAi);
            this.enableUrlPreview$.next(value.enableUrlPreview);
            this.appBasesEnabled$.next(value.appBasesEnabled);
            this.presenceDiscoveryEnabled$.next(
              this.realtime.presenceDiscoveryEnabled$.value ??
                value.presenceDiscoveryEnabled
            );
          }
        }),
        catchErrorInto(this.error$, error => {
          logger.error('Failed to fetch enableUrlPreview', error);
        }),
        onStart(() => this.isLoading$.setValue(true)),
        onComplete(() => this.isLoading$.setValue(false))
      );
    })
  );

  revalidateInviteLink = effect(
    exhaustMap(() =>
      fromPromise(signal =>
        this.store.fetchWorkspaceInviteLink(
          this.workspaceService.workspace.id,
          signal
        )
      ).pipe(
        tap(value => {
          this.inviteLink$.next(value);
        }),
        catchErrorInto(this.error$, error => {
          logger.error('Failed to fetch workspace invite link', error);
        })
      )
    )
  );

  async waitForRevalidation(signal?: AbortSignal) {
    // Set synchronously BEFORE triggering the async pipeline.
    // exhaustMapWithTrailing uses asyncScheduler — without this,
    // waitFor resolves immediately before the fetch starts.
    this.isLoading$.setValue(true);
    this.revalidate();
    await this.isLoading$.waitFor(isLoading => !isLoading, signal);
  }

  async setEnableUrlPreview(enableUrlPreview: EnableUrlPreview) {
    await this.store.updateWorkspaceEnableUrlPreview(
      this.workspaceService.workspace.id,
      enableUrlPreview
    );
    await this.waitForRevalidation();
  }

  async setEnableAi(enableAi: EnableAi) {
    await this.store.updateWorkspaceEnableAi(
      this.workspaceService.workspace.id,
      enableAi
    );
    await this.waitForRevalidation();
  }

  // sl-xm1c.2.4: Workspace-Admin Toggle for the AppBases feature
  async setAppBasesEnabled(appBasesEnabled: AppBasesEnabled) {
    await this.store.updateWorkspaceAppBasesEnabled(
      this.workspaceService.workspace.id,
      appBasesEnabled
    );
    await this.waitForRevalidation();
  }

  async revalidatePresencePolicy(signal?: AbortSignal) {
    const enabled = await this.store.fetchPresenceDiscoveryPolicy(
      this.workspaceService.workspace.id,
      signal
    );
    if (!signal?.aborted) this.presenceDiscoveryEnabled$.next(enabled);
    return enabled;
  }

  async setPresenceDiscoveryEnabled(enabled: boolean) {
    const saved = await this.store.updatePresenceDiscovery(
      this.workspaceService.workspace.id,
      enabled
    );
    this.presenceDiscoveryEnabled$.next(saved);
  }

  override dispose(): void {
    this.revalidate.unsubscribe();
    this.revalidateInviteLink.unsubscribe();
    super.dispose();
  }
}
